import { useCallback, useEffect, useState } from 'react';

import { fetchCardObjectUrl } from '@/api/labeling';

export type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; url: string; contentType: string }
  | { status: 'error' };

/**
 * Fetch a labeling card's bytes as an object URL for the preview pane.
 * Exposes an explicit `error` status (distinct from `loading`) with a
 * `retry`, and revokes the object URL when the card changes or unmounts.
 * Extracted from the route so the loading/ready/error transitions are
 * unit-testable (INP-651).
 */
export function useCardPreview(
  documentId: string,
  getIdToken: () => Promise<string | null>,
): { state: PreviewState; retry: () => void } {
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ status: 'loading' });
    (async () => {
      try {
        const result = await fetchCardObjectUrl(documentId, (await getIdToken()) ?? '');
        if (cancelled) {
          URL.revokeObjectURL(result.url);
          return;
        }
        objectUrl = result.url;
        setState({ status: 'ready', url: result.url, contentType: result.contentType });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, getIdToken, attempt]);

  return { state, retry };
}
